/**
 * Engine - The main game orchestrator.
 * Wires all systems, entities, and UI, then drives the game loop.
 * Updated to wire all new systems: Audio, Save, Localization, Weather,
 * MatchStats, ScreenShake, Halftime, Settings, Credits.
 */

import * as BABYLON from "babylonjs";
import { SceneManager, FIELD_LENGTH } from "./SceneManager";
import { RoundedStadium } from "./RoundedStadium";
import { PhysicsSystem } from "../systems/PhysicsSystem";
import { InputSystem } from "../systems/InputSystem";
import { GameState, State } from "../systems/GameState";
import { ScoreSystem } from "../systems/ScoreSystem";
import { AudioSystem } from "../systems/AudioSystem";
import { SaveSystem } from "../systems/SaveSystem";
import { LocalizationSystem } from "../systems/LocalizationSystem";
import { WeatherSystem } from "../systems/WeatherSystem";
import { MatchStatsSystem } from "../systems/MatchStatsSystem";
import { ScreenShakeSystem } from "../systems/ScreenShakeSystem";
import { SetPieceSystem, SetPieceInfo } from "../systems/SetPieceSystem";
import { GrassParticleSystem } from "../systems/GrassParticleSystem";
import { TournamentSystem, GameMode } from "../systems/TournamentSystem";
import { TeamSelectionScreen } from "../ui/TeamSelectionScreen";
import { Player } from "../entities/Player";
import { Ball } from "../entities/Ball";
import { Goal, GOAL_HALF_WIDTH } from "../entities/Goal";
import { Goalkeeper } from "../entities/Goalkeeper";
import { HUD } from "../ui/HUD";
import { Menu } from "../ui/Menu";
import { EndScreen } from "../ui/EndScreen";
import { GoalOverlay } from "../ui/GoalOverlay";
import { Radar } from "../ui/Radar";
import { HalftimeOverlay } from "../ui/HalftimeOverlay";
import { SettingsMenu } from "../ui/SettingsMenu";
import { CreditsScreen } from "../ui/CreditsScreen";
import { CelebrationType } from "../entities/HumanoidMesh";
import { DifficultyManager, DifficultyLevel, AIRole } from "../systems/DifficultyManager";
import { BotBase } from "../entities/BotBase";
import { ChaserBot } from "../entities/ChaserBot";
import { DefenderBot } from "../entities/DefenderBot";
import { StrikerBot } from "../entities/StrikerBot";
import { MidfielderBot } from "../entities/MidfielderBot";
import { DefenderFormationBot } from "../entities/DefenderFormationBot";
import { MidfielderFormationBot } from "../entities/MidfielderFormationBot";
import { ForwardFormationBot } from "../entities/ForwardFormationBot";

export class Engine {
  private _engine: BABYLON.Engine;
  private _scene!: BABYLON.Scene;
  private _camera!: BABYLON.FollowCamera;
  private _shadowGenerator!: BABYLON.ShadowGenerator;

  // Core systems
  private _input = new InputSystem();
  private _gameState = new GameState();
  private _scoreSystem!: ScoreSystem;
  private _matchStats = new MatchStatsSystem();
  private _weather?: WeatherSystem;
  private _screenShake?: ScreenShakeSystem;

  // Entities
  private _player!: Player;
  private _ball!: Ball;
  private _bots: BotBase[] = [];
  private _goalkeepers: Goalkeeper[] = [];
  private _difficulty: DifficultyLevel = DifficultyLevel.EASY;

  // UI
  private _hud!: HUD;
  private _menu!: Menu;
  private _endScreen!: EndScreen;
  private _goalOverlay!: GoalOverlay;
  private _radar!: Radar;
  private _halftimeOverlay!: HalftimeOverlay;
  private _settingsMenu!: SettingsMenu;
  private _creditsScreen!: CreditsScreen;

  // Game mode
  private _isPracticeMode = false;

  // ESC menu toggle
  private _wasEscDown = false;
  private _isMenuOpen = false;

  // Halftime state
  private _inHalftimeBreak = false;
  private _halftimeBreakTimer = 0;
  private static readonly HALFTIME_BREAK_DURATION = 5; // seconds

  // Right-mouse camera orbit
  private _rmb = false;
  private _lastMouseX = 0;
  private _lastMouseY = 0;

  // Camera target values for smoothing
  private _targetRotationOffset = 180;
  private _targetHeightOffset = 11;
  private _targetRadius = 16;

  // Footstep tracking
  private _footstepTimer = 0;

  // Frame rate limiting
  private _fpsLimit = 0;
  private _lastFrameTime = 0;

  constructor(canvas: HTMLCanvasElement) {
    this._engine = new BABYLON.Engine(canvas, true, {
      preserveDrawingBuffer: true,
      stencil: true,
      antialias: true,
      premultipliedAlpha: false,
    });

    // Force high DPI rendering
    this._engine.setHardwareScalingLevel(1.0 / window.devicePixelRatio);

    // Load preferences first
    this._loadPreferences();

    this._init(canvas);

    window.addEventListener("resize", () => this._engine.resize());

    // Fix: handle tab visibility (task 9.12)
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        if (this._gameState.state === State.Playing && !this._isMenuOpen) {
          this._pauseGame();
        }
      } else {
        AudioSystem.resume();
        this._engine.getRenderingCanvas()?.focus();
      }
    });

    // Fix: fullscreen toggle (task 9.11)
    document.addEventListener("keydown", (e) => {
      if (e.code === "F11" || (e.code === "KeyF" && e.altKey)) {
        this._toggleFullscreen(canvas);
        e.preventDefault();
      }
    });
  }

  private _loadPreferences(): void {
    const prefs = SaveSystem.getPreferences();

    // Load language
    LocalizationSystem.setLanguage(prefs.language || "en");

    // Load audio settings
    AudioSystem.init();
    AudioSystem.setMasterVolume(prefs.masterVolume ?? 0.8);
    AudioSystem.setSFXVolume(prefs.sfxVolume ?? 1.0);
    AudioSystem.setMusicVolume(prefs.musicVolume ?? 0.6);
    AudioSystem.setCommentaryVolume(prefs.commentaryVolume ?? 0.9);

    // Load frame rate limit
    this._fpsLimit = prefs.frameRateLimit ?? 0;

    // Load match duration
    if (prefs.matchDuration) {
      this._gameState.setMatchDuration(prefs.matchDuration);
    }

    // Load difficulty
    if (prefs.difficulty) {
      const diffMap: Record<string, DifficultyLevel> = {
        easy: DifficultyLevel.EASY,
        medium: DifficultyLevel.MEDIUM,
        hard: DifficultyLevel.HARD,
        impossible: DifficultyLevel.IMPOSSIBLE,
      };
      this._difficulty = diffMap[prefs.difficulty] ?? DifficultyLevel.EASY;
    }

    console.log("Preferences loaded:", prefs.language, prefs.masterVolume);
  }

  private _toggleFullscreen(canvas: HTMLCanvasElement): void {
    if (!document.fullscreenElement) {
      canvas.requestFullscreen().catch(err => console.warn("Fullscreen failed:", err));
    } else {
      document.exitFullscreen();
    }
  }

  private _pauseGame(): void {
    if (this._gameState.state === State.Playing && !this._isMenuOpen) {
      this._toggleMenu();
    }
  }

  private async _init(canvas: HTMLCanvasElement): Promise<void> {
    await this._buildScene(canvas);
    this._buildUI();

    // Load improved crowd model
    await RoundedStadium.loadCrowdModel(this._scene);
    RoundedStadium.createRoundedCrowd(this._scene);

    SceneManager.setCrowdVisible(true);
    this._setupCameraOrbit(canvas);

    // Apply saved weather
    const prefs = SaveSystem.getPreferences();
    if (this._weather && prefs.weather !== "none") {
      this._weather.setWeather(prefs.weather);
    }

    this._startRenderLoop();
  }

  // ─── Right-mouse camera orbit ───────────────────────────────────

  private _setupCameraOrbit(canvas: HTMLCanvasElement): void {
    canvas.addEventListener("pointerdown", (e) => {
      if (e.button === 2) {
        this._rmb = true;
        this._lastMouseX = e.clientX;
        this._lastMouseY = e.clientY;
      }
    });
    canvas.addEventListener("pointerup", (e) => {
      if (e.button === 2) this._rmb = false;
    });
    canvas.addEventListener("pointermove", (e) => {
      if (!this._rmb) return;
      const dx = e.clientX - this._lastMouseX;
      const dy = e.clientY - this._lastMouseY;

      this._targetRotationOffset += dx * 0.6;
      this._targetRotationOffset = ((this._targetRotationOffset % 360) + 360) % 360;

      this._targetHeightOffset = BABYLON.Scalar.Clamp(
        this._targetHeightOffset - dy * 0.12,
        0.5,
        35
      );

      this._lastMouseX = e.clientX;
      this._lastMouseY = e.clientY;
    });

    canvas.addEventListener("wheel", (e) => {
      const zoomFactor = 1 + (Math.sign(e.deltaY) * 0.15);
      this._targetRadius = BABYLON.Scalar.Clamp(
        this._targetRadius * zoomFactor,
        2,
        60
      );
      e.preventDefault();
    }, { passive: false });

    canvas.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  // ─── Scene construction ───────────────────────────────────────────

  private async _buildScene(canvas: HTMLCanvasElement): Promise<void> {
    const { scene, camera, shadowGenerator } = SceneManager.create(
      this._engine,
      canvas
    );
    this._scene = scene;
    this._camera = camera;
    this._shadowGenerator = shadowGenerator;

    // Initialize camera targets from current values
    this._targetRotationOffset = camera.rotationOffset;
    this._targetHeightOffset = camera.heightOffset;
    this._targetRadius = camera.radius;

    // Screen shake system (task 6.13)
    this._screenShake = new ScreenShakeSystem(camera);

    // Weather system (task 6.7)
    this._weather = new WeatherSystem(scene);

    // Initialize physics
    new PhysicsSystem(scene);
    await this._waitForPhysics(scene);

    SceneManager.addWallPhysics(scene);

    // Ground impostor
    const ground = scene.getMeshByName("field") as BABYLON.Mesh;
    if (ground) {
      ground.physicsImpostor = new BABYLON.PhysicsImpostor(
        ground,
        BABYLON.PhysicsImpostor.BoxImpostor,
        { mass: 0, restitution: 0.3, friction: 0.9 },
        scene
      );
    }

    // Entities
    this._ball = new Ball(scene, shadowGenerator);
    this._player = new Player(scene, this._input, this._ball, camera, shadowGenerator);

    const goalZ = FIELD_LENGTH / 2;
    new Goal(scene, goalZ, shadowGenerator);
    new Goal(scene, -goalZ, shadowGenerator);

    // Goalkeepers
    const opponentGK = new Goalkeeper(
      scene, this._ball, goalZ - 0.25,
      new BABYLON.Color3(0.1, 0.8, 0.1),
      shadowGenerator,
      true
    );
    this._goalkeepers.push(opponentGK);

    const playerGK = new Goalkeeper(
      scene, this._ball, -goalZ + 0.25,
      new BABYLON.Color3(0.9, 0.6, 0.0),
      shadowGenerator,
      false
    );
    this._goalkeepers.push(playerGK);

    opponentGK.setTeammateProvider(() => this._bots.map(b => b.mesh.position));
    playerGK.setTeammateProvider(() => [this._player.mesh.position]);

    // Camera target
    const camTarget = BABYLON.MeshBuilder.CreateBox("camTarget", { size: 0.01 }, scene);
    camTarget.isVisible = false;
    scene.onBeforeRenderObservable.add(() => {
      camTarget.position.copyFrom(this._player.mesh.position);
    });
    this._camera.lockedTarget = camTarget;

    // Score system
    this._scoreSystem = new ScoreSystem(
      this._gameState,
      this._ball.mesh,
      goalZ,
      -goalZ,
      GOAL_HALF_WIDTH,
      (side) => this._onGoal(side)
    );
  }

  private async _waitForPhysics(scene: BABYLON.Scene): Promise<void> {
    await new Promise<void>((resolve) => {
      const check = () => {
        const engine = scene.getPhysicsEngine();
        const plugin = engine?.getPhysicsPlugin?.() as any;
        if (engine && plugin && plugin.world) {
          resolve();
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
    await new Promise(resolve => setTimeout(resolve, 200));
    console.log("Physics engine ready!");
  }

  // ─── UI construction ──────────────────────────────────────────────

  private _buildUI(): void {
    this._hud = new HUD(this._gameState);
    this._hud.setVisible(false);

    this._menu = new Menu(
      this._scene,
      () => this._startPractice(),
      () => this._startMatch(),
      (show) => {
        this._gameState.showCrowd = show;
        SceneManager.setCrowdVisible(show);
      },
      (difficulty: DifficultyLevel) => this._startMatchWithDifficulty(difficulty)
    );

    this._endScreen = new EndScreen(() => this._startMatchWithDifficulty(this._difficulty));
    this._goalOverlay = new GoalOverlay();
    this._radar = new Radar();
    this._radar.setVisible(false);

    // Halftime overlay (task 1.14, 3.4)
    this._halftimeOverlay = new HalftimeOverlay();

    // Settings menu (task 10.6)
    this._settingsMenu = new SettingsMenu(
      () => {
        // On settings close - re-apply preferences
        this._applyPreferences();
      },
      this._weather,
      (prefs) => this._applyPreferences()
    );

    // Credits screen (task 10.5)
    this._creditsScreen = new CreditsScreen(() => {});
  }

  private _applyPreferences(): void {
    const prefs = SaveSystem.getPreferences();

    // Apply frame rate limit
    this._fpsLimit = prefs.frameRateLimit ?? 0;

    // Apply match duration
    if (prefs.matchDuration) {
      this._gameState.setMatchDuration(prefs.matchDuration);
    }

    // Apply HUD visibility preferences
    this._hud.setControlsHintVisible(prefs.showControls ?? true);

    // Apply radar visibility
    if (this._gameState.state === State.Playing) {
      this._radar.setVisible(prefs.showRadar ?? true);
    }

    // Apply weather
    if (this._weather) {
      this._weather.setWeather(prefs.weather ?? "none");
    }

    // Apply crowd setting
    SceneManager.setCrowdVisible(prefs.highPerformanceCrowd ?? true);

    // Apply language
    LocalizationSystem.setLanguage(prefs.language ?? "en");

    console.log("Preferences applied");
  }

  // ─── ESC Menu Toggle ──────────────────────────────────────────────

  private _toggleMenu(): void {
    this._isMenuOpen = !this._isMenuOpen;

    if (this._isMenuOpen) {
      this._menu.setVisible(true);
      this._hud.setVisible(false);
      this._radar.setVisible(false);
    } else {
      this._menu.setVisible(false);
      this._hud.setVisible(true);
      const prefs = SaveSystem.getPreferences();
      this._radar.setVisible(prefs.showRadar ?? true);
      this._engine.getRenderingCanvas()?.focus();
    }
  }

  // ─── Game flow ────────────────────────────────────────────────────

  private _startPractice(): void {
    this._isPracticeMode = true;
    this._isMenuOpen = false;
    this._startGame();
  }

  private _startMatch(): void {
    this._isPracticeMode = false;
    this._isMenuOpen = false;
    this._startGame();
  }

  private _startMatchWithDifficulty(difficulty: DifficultyLevel): void {
    console.log(`Starting match with difficulty: ${difficulty}`);
    this._difficulty = difficulty;
    this._isPracticeMode = false;
    this._isMenuOpen = false;

    DifficultyManager.setDifficulty(difficulty);
    this._gameState.setDifficulty(difficulty);

    // Save difficulty preference
    SaveSystem.setPreference("difficulty", difficulty);

    this._startGame();
  }

  private _startGame(): void {
    console.log("Starting game... practice:", this._isPracticeMode, "difficulty:", this._difficulty);

    // Apply current preferences
    this._applyPreferences();

    this._gameState.reset();
    this._gameState.setState(State.Playing);
    this._matchStats.reset();
    this._inHalftimeBreak = false;
    this._halftimeBreakTimer = 0;

    // Dispose existing bots
    this._bots.forEach((bot) => bot.mesh.dispose());
    this._bots = [];

    // Create bots
    if (!this._isPracticeMode) {
      const botConfigs = DifficultyManager.getBotConfigs();

      for (const config of botConfigs) {
        let bot: BotBase;

        switch (config.role) {
          case AIRole.CHASER:
            bot = new ChaserBot(this._scene, this._ball, config.homePosition, config.allowedZone, config.separationConfig, this._shadowGenerator, this._player);
            break;
          case AIRole.DEFENDER:
            bot = new DefenderBot(this._scene, this._ball, config.homePosition, config.allowedZone, config.separationConfig, this._shadowGenerator, this._player);
            break;
          case AIRole.STRIKER:
            bot = new StrikerBot(this._scene, this._ball, config.homePosition, config.allowedZone, config.separationConfig, this._shadowGenerator, this._player);
            break;
          case AIRole.MIDFIELDER:
            bot = new MidfielderBot(this._scene, this._ball, config.homePosition, config.allowedZone, config.separationConfig, this._shadowGenerator, this._player);
            break;
          case AIRole.LEFT_BACK:
          case AIRole.CENTER_BACK_LEFT:
          case AIRole.CENTER_BACK_RIGHT:
          case AIRole.RIGHT_BACK:
            bot = new DefenderFormationBot(this._scene, this._ball, config.role, config.homePosition, config.allowedZone, config.separationConfig, this._shadowGenerator, this._player);
            break;
          case AIRole.DEFENSIVE_MID:
          case AIRole.CENTRAL_MID:
          case AIRole.ATTACKING_MID:
            bot = new MidfielderFormationBot(this._scene, this._ball, config.role, config.homePosition, config.allowedZone, config.separationConfig, this._shadowGenerator, this._player);
            break;
          case AIRole.LEFT_WINGER:
          case AIRole.RIGHT_WINGER:
          case AIRole.CENTER_FORWARD:
            bot = new ForwardFormationBot(this._scene, this._ball, config.role, config.homePosition, config.allowedZone, config.separationConfig, this._shadowGenerator, this._player);
            break;
          default:
            console.warn(`Unknown bot role: ${config.role}`);
            continue;
        }

        this._bots.push(bot);
      }

      // Set up coordination chain
      const defender = this._bots.find(b => b.role === AIRole.DEFENDER) as DefenderBot;
      const midfielder = this._bots.find(b => b.role === AIRole.MIDFIELDER) as MidfielderBot;
      const striker = this._bots.find(b => b.role === AIRole.STRIKER);

      if (this._difficulty === DifficultyLevel.HARD) {
        if (defender && midfielder) defender.setStrikerPosition(midfielder.mesh.position);
        if (midfielder && striker) midfielder.setStrikerPosition(striker.mesh.position);
      } else {
        if (defender && striker) defender.setStrikerPosition(striker.mesh.position);
      }

      console.log(`Created ${this._bots.length} bots for difficulty ${this._difficulty}`);
    }

    this._ball.resetPosition();
    this._player.resetPosition();
    this._bots.forEach((bot) => bot.resetPosition());
    this._goalkeepers.forEach((gk) => gk.resetPosition());

    this._menu.setVisible(false);
    this._endScreen.hide();
    this._halftimeOverlay.hide();
    this._hud.setVisible(true);

    const prefs = SaveSystem.getPreferences();
    this._radar.setVisible(prefs.showRadar ?? true);

    // Play kickoff whistle
    AudioSystem.playWhistle(false);

    this._engine.getRenderingCanvas()?.focus();
  }

  private _onGoal(side: "player" | "opponent"): void {
    console.log(`Goal scored by: ${side}`);

    // Audio
    AudioSystem.playGoal();
    AudioSystem.playCommentary(side === "player" ? "goal" : "opponentGoal");

    // Screen shake (task 6.13)
    this._screenShake?.goalShake();

    // Track shot on target
    this._matchStats.recordShot(side, true);

    this._goalOverlay.show(side);
    SceneManager.triggerGoalCelebration();

    // Disable AI ball interaction during celebration
    this._bots.forEach(bot => bot.isGoalCelebration = true);
    this._goalkeepers.forEach(gk => {
      gk.isGoalCelebration = true;
      gk.resetPosition();
    });

    this._ball.mesh.parent = null;
    this._ball.resetPosition();

    if (side === "player") {
      const cornerTarget = new BABYLON.Vector3(18, 0, 28);
      this._player.celebrateAt(cornerTarget);
    } else {
      this._bots.forEach(bot => bot.celebrate());
      this._goalkeepers.forEach(gk => gk.animator.triggerCelebration(CelebrationType.Jump));
    }

    setTimeout(() => {
      this._bots.forEach(bot => bot.isGoalCelebration = false);
      this._goalkeepers.forEach(gk => gk.isGoalCelebration = false);
      this._player.resetPosition();
      this._bots.forEach((bot) => bot.resetPosition());
      this._goalkeepers.forEach((gk) => gk.resetPosition());

      // Kickoff whistle after goal
      AudioSystem.playWhistle(false);
    }, 5000);
  }

  private _onHalftime(): void {
    console.log("HALFTIME!");
    this._inHalftimeBreak = true;
    this._halftimeBreakTimer = Engine.HALFTIME_BREAK_DURATION;

    // Audio
    AudioSystem.playWhistle(true);
    AudioSystem.playCommentary("save"); // Halftime commentary

    // Show halftime overlay
    this._halftimeOverlay.show(
      this._gameState.firstHalfScore,
      this._matchStats.getStats()
    );
    this._hud.setVisible(false);
    this._radar.setVisible(false);
  }

  private _onExtraTime(): void {
    console.log("EXTRA TIME!");
    this._goalOverlay.show("player"); // Reuse overlay for announcements
    AudioSystem.playWhistle(false);
    AudioSystem.playRefereeBeep();
  }

  private _endMatch(): void {
    this._gameState.setState(State.GameOver);
    this._hud.setVisible(false);
    this._radar.setVisible(false);
    this._halftimeOverlay.hide();

    // Play final whistle
    AudioSystem.playWhistle(true);

    // Show end screen with full stats
    this._endScreen.show(
      this._gameState.score,
      this._matchStats.getStats(),
      this._difficulty
    );
  }

  // ─── Render / update loop ─────────────────────────────────────────

  private _startRenderLoop(): void {
    const defenderPos: BABYLON.Vector3[] = [];
    const gkPos: BABYLON.Vector3[] = [];
    const prefs = SaveSystem.getPreferences();

    this._engine.runRenderLoop(() => {
      // Frame rate limiting (task 8.7)
      if (this._fpsLimit > 0) {
        const now = performance.now();
        const minInterval = 1000 / this._fpsLimit;
        if (now - this._lastFrameTime < minInterval) {
          return;
        }
        this._lastFrameTime = now;
      }

      let dt = this._engine.getDeltaTime() / 1000;
      if (dt > 0.05) dt = 0.05;

      // Poll gamepad (task 7.1)
      this._input.pollGamepad();

      // Audio resume on user activity
      AudioSystem.resume();

      // Handle ESC key (one-shot)
      const escDown = this._input.escape;
      if (escDown && !this._wasEscDown) {
        if (this._settingsMenu.isVisible) {
          this._settingsMenu.setVisible(false);
        } else if (this._gameState.state === State.Playing && !this._inHalftimeBreak) {
          this._toggleMenu();
        }
      }
      this._wasEscDown = escDown;

      // Smooth camera transitions
      const lerpFactor = Math.min(1, dt * 15);
      let target = ((this._targetRotationOffset % 360) + 360) % 360;
      let current = ((this._camera.rotationOffset % 360) + 360) % 360;
      if (isNaN(current)) current = 0;

      let diff = target - current;
      if (diff > 180) diff -= 360;
      if (diff < -180) diff += 360;

      this._camera.rotationOffset = current + diff * lerpFactor;
      this._camera.heightOffset += (this._targetHeightOffset - this._camera.heightOffset) * lerpFactor;
      this._camera.radius += (this._targetRadius - this._camera.radius) * lerpFactor;

      // Screen shake (task 6.13)
      this._screenShake?.update(dt);

      // Handle halftime break countdown
      if (this._inHalftimeBreak) {
        this._halftimeBreakTimer -= dt;
        this._halftimeOverlay.updateCountdown(Math.max(0, this._halftimeBreakTimer));

        if (this._halftimeBreakTimer <= 0) {
          this._inHalftimeBreak = false;
          this._halftimeOverlay.hide();
          this._hud.setVisible(true);
          const savedPrefs = SaveSystem.getPreferences();
          this._radar.setVisible(savedPrefs.showRadar ?? true);
          this._gameState.setState(State.Playing);
          // Reset positions for second half
          this._ball.resetPosition();
          this._player.resetPosition();
          this._bots.forEach(b => b.resetPosition());
          this._goalkeepers.forEach(gk => gk.resetPosition());
          AudioSystem.playWhistle(false);
        }
        this._scene.render();
        return;
      }

      if (this._gameState.state === State.Playing && !this._isMenuOpen) {
        const tickResult = this._gameState.tick(dt);

        if (tickResult === "halftime") {
          this._gameState.setState(State.Paused);
          this._onHalftime();
        } else if (tickResult === "extratime") {
          this._onExtraTime();
        } else if (tickResult === "gameover") {
          this._endMatch();
          this._scene.render();
          return;
        }

        // Entities
        this._player.update(dt);
        this._ball.update(dt);
        this._bots.forEach((bot) => bot.update(dt, this._bots));
        this._goalkeepers.forEach((gk) => gk.update(dt));

        // Crowd
        SceneManager.update(dt, this._ball.mesh.position, this._camera.rotationOffset);

        // Scoring
        this._scoreSystem.update(dt);

        // Possession tracking (task 3.13)
        const holder = this._player.hasBallPossession ? "player" :
          this._bots.some(b => {
            const d = BABYLON.Vector3.Distance(b.mesh.position, this._ball.mesh.position);
            return d < 2.0;
          }) ? "opponent" : "none";
        this._matchStats.updatePossession(holder, dt);

        // Footstep sounds (task 4.6)
        if (this._player.stamina < 150) { // Player is moving
          this._footstepTimer -= dt;
          if (this._footstepTimer <= 0 && this._gameState.state === State.Playing) {
            this._footstepTimer = 0.35;
            // Play footstep occasionally
          }
        }

        // HUD update with new stats (task 3.1, 3.13, 3.14, 3.15)
        const staminaPct = (this._player.stamina / 150) * 100;
        const savedPrefs = SaveSystem.getPreferences();
        this._hud.update(
          staminaPct,
          this._player.kickCharge,
          savedPrefs.showPossession !== false ? this._matchStats.getPlayerPossessionPercent() : 50,
          savedPrefs.showShotsOnTarget !== false ? this._matchStats.getPlayerShotsOnTarget() : 0,
          savedPrefs.showShotsOnTarget !== false ? this._matchStats.getOpponentShotsOnTarget() : 0,
          this._input.isAutoPassEnabled
        );

        // Goal celebration overlay
        this._goalOverlay.update(dt);

        // Radar
        defenderPos.length = 0;
        for (const bot of this._bots) defenderPos.push(bot.mesh.position);
        gkPos.length = 0;
        for (const gk of this._goalkeepers) gkPos.push(gk.mesh.position);

        this._radar.update(
          this._player.mesh.position,
          this._ball.mesh.position,
          defenderPos,
          gkPos
        );
      }

      this._scene.render();
    });
  }
}
